import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "../../Context/UserContext";
import useAPI from "../../Hooks/useAPI";
import {
  PROFESSIONS,
  INTERESTS,
  Profession,
  Interest,
} from "../../constants/userPreferences";
import "../../Components/Page/Page.css";
import "./UserForm.css";
import Header2 from "../../Components/Header2/Header2";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import debounce from "lodash.debounce";

interface UserFormData {
  id?: string; // Optional ID for user identification
  email: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  streetaddress?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  companyName?: string;
  companyType?: string;
  title?: string;
  profession?: string[];
  professionOther?: string;
  interests?: string[];
  interestsOther?: string;
  roles: string[];
  password?: string;
  lastLoginAt?: string;
  mndaStatus?: string;
  entityid?: string;
  cidn?: number;
}

interface Role {
  id: string;
  name: string;
}

// Define props for States
interface StateOption {
  stateprovinceabbrv: string;
  stateprovincename: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  profession?: string;
  interests?: string;
  agreeToTerms?: string;
  general?: string;
}

export default function UserForm() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useUser();
  const {
    getUserById,
    createUser,
    updateUser,
    getRoles,
    getUserRoles,
    addUserTracking,
    getStateProvince,
    saveContact,
    checkEmailExists,
  } = useAPI();
  const [error, setError] = useState<string>("");
  const [errors, setFormErrors] = useState<FormErrors>({});
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [states, setStates] = useState<StateOption[]>([]);
  const [countrycode, setCountryCode] = useState("");
  const [, setPhoneNoCode] = useState("");
  const [cidn, setCIDN] = useState<number | null>(null);
  const [, setEmail] = useState("");
  const [, setFirstName] = useState("");
  const [, setMiddleName] = useState("");
  const [, setLastName] = useState("");
  const [, setAddress] = useState("");
  const [, setCity] = useState("");
  const [, setState] = useState("");
  const [, setZip] = useState("");
  const [, setCountry] = useState("");
  const [, setPhone] = useState("");
  const [, setCompanyName] = useState("");
  const [, setCompanyType] = useState("");
  const [, setTitle] = useState("");
  const [, setDataSourceDescription] = useState("");
  const [, setProfession] = useState<string[]>([]);
  const [, setProfessionOther] = useState("");
  const [, setInterests] = useState<string[]>([]);
  const [, setInterestsOther] = useState("");
  const [, setAgreeToTerms] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const [formData, setFormData] = useState<UserFormData>({
    email: "",
    firstName: "",
    middleName: "",
    lastName: "",
    streetaddress: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    phone: "",
    companyName: "",
    companyType: "",
    title: "",
    profession: [],
    professionOther: "",
    interests: [],
    interestsOther: "",
    roles: [],
    password: "",
    lastLoginAt: "",
    mndaStatus: "",
    entityid: "",
  });
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const isNewUser = !userId;

  const fetchUser = useCallback(async () => {
    try {
      if (!userId) return;

      const response = await getUserById(userId);

      if (response?.data) {
        const userData = {
          id: userId,
          email: response.data.email,
          firstName: response.data.firstName,
          middleName: response.data.middleName,
          lastName: response.data.lastName,
          streetaddress: response.data.streetaddress,
          city: response.data.city,
          state: response.data.state,
          zip: response.data.zip,
          country: response.data.country,
          phone: response.data.phone,
          companyName: response.data.companyName,
          companyType: response.data.companyType,
          title: response.data.title,
          entityid: response.data.entityid,
          profession: (response.data.profession || []) as Profession[],
          professionOther: response.data.professionOther,
          interests: (response.data.interests || []) as Interest[],
          interestsOther: response.data.interestsOther,
          lastLoginAt: response.data.lastLoginAt,
          mndaStatus: response.data.mndaStatus,
          roles: [],
          activeind: response.data.activeind,
          cidn: response.data.cidn,
        };

        setFormData(userData);

        if (response.data.cidn) {
          setCIDN(response.data.cidn);
        }

        const userRoles = await getUserRoles(userId);

        if (userRoles && Array.isArray(userRoles)) {
          const roleIds = userRoles.map((role) =>
            typeof role === "string" ? role : role.id
          );

          setSelectedRoles(roleIds);

          setFormData((prev) => ({
            ...prev,
            roles: roleIds,
          }));
        }

        setIsDataLoaded(true);
      }
    } catch (err) {
      console.error("Error fetching user:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  }, [userId, getUserById, getUserRoles]);

  const fetchRoles = useCallback(async () => {
    try {
      const rolesData = await getRoles();

      if (rolesData && Array.isArray(rolesData)) {
        setRoles(rolesData);
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
      setError(err instanceof Error ? err.message : "Failed to load roles");
    }
  }, [getRoles]);

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await getStateProvince();
        setStates(response);
      } catch (error) {
        console.error("Error loading states:", error);
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (isNewUser && roles.length > 0 && selectedRoles.length === 0) {
      const registeredRole = roles.find(
        (role: Role) => role.name.toUpperCase() === "USER-REGISTERED"
      );

      if (registeredRole) {
        setSelectedRoles([registeredRole.id]);
      }
    }
  }, [isNewUser, roles, selectedRoles.length]);

  useEffect(() => {
    if (!hasRole("administrator")) {
      navigate("/");
      return;
    }

    fetchRoles();

    if (!isNewUser) {
      fetchUser();
    } else {
      setIsDataLoaded(true);
    }
  }, [user?.roles, navigate, isNewUser, fetchUser, fetchRoles]);

  useEffect(() => {
    if (isDataLoaded) {
      setFormData((prev) => ({
        ...prev,
        roles: selectedRoles.map((id) => String(id)), // Ensure all role IDs are strings
      }));
    }
  }, [selectedRoles, isDataLoaded, hasRole]);

  const debouncedSaveRef = React.useRef(
    debounce(
      async (
        updatedData: UserFormData,
        cidn: Number | null,
        fieldname: string | null
      ) => {
        try {
          //console.log("payload: ", updatedData);

          const bReturnAll = fieldname === "email"; //set breturnall to true if fieldname is email

          console.log("debounced data: ", updatedData);
          console.log("cidn parameter: ", cidn);

          const response = await saveContact(
            {
              ...updatedData,
              datasource: "userregistration",
            },
            bReturnAll,
            cidn
          );

          const out_cidn = response.out_cidn;

          //Only update all the fields in the page if we have a match by email address
          if (bReturnAll) {
            const returnedEmail = response?.email;
            const returnedFirstName = response?.firstname;
            const returnedMiddleName = response?.middlename;
            const returnedLastName = response?.lastname;
            const returnedAddress = response?.address1;
            const returnedCity = response?.city;
            const returnedState = response?.state;
            const returnedZip = response?.zip;
            const returnedCountry = response?.country;
            const returnedPhone = response?.phone;
            const returnedCountryCode = response?.countrycode;
            const returnedCompanyName = response?.companyname;
            const returnedCompanyType = response?.companytype;
            const returnedTitle = response?.title;
            const returnedDatasource = response?.datasourcedescription;
            const returnedProfession = response?.profession;
            const returnedProfessionOther = response?.profession_other;
            const returnedInterests = response?.interests;
            const returnedInterestsOther = response?.interests_other;
            const returnedAgreeToTerms = response?.termsconditions;

            const professionArray = parsePgArray(returnedProfession);
            const interestsArray = parsePgArray(returnedInterests);

            setEmail(returnedEmail);
            setFirstName(returnedFirstName);
            setMiddleName(returnedMiddleName);
            setLastName(returnedLastName);
            setAddress(returnedAddress);
            setCity(returnedCity);
            setState(returnedState);
            setZip(returnedZip);
            setCountry(returnedCountry);
            setPhone(returnedPhone);
            setPhoneNoCode(returnedPhone);
            if (returnedCountryCode && returnedPhone) {
              setPhoneNumber(`${returnedCountryCode}${returnedPhone}`);
            }
            setCountryCode(returnedCountryCode);
            setCompanyName(returnedCompanyName);
            setCompanyType(returnedCompanyType);
            setTitle(returnedTitle);
            setDataSourceDescription(returnedDatasource);
            setProfession(professionArray);
            setProfessionOther(returnedProfessionOther);
            setInterests(interestsArray);
            setInterestsOther(returnedInterestsOther);
            setAgreeToTerms(returnedAgreeToTerms);

            // Update the form field so the input shows it
            setFormData((prev) => ({
              ...prev,
              email: returnedEmail || "",
              firstName: returnedFirstName || "",
              middleName: returnedMiddleName || "",
              lastName: returnedLastName || "",
              streetaddress: returnedAddress || "",
              city: returnedCity || "",
              state: returnedState || "",
              zip: returnedZip || "",
              country: returnedCountry || "",
              phone: returnedPhone || "",
              phoneNumber: returnedPhone || "",
              countrycode: returnedCountryCode || "",
              companyName: returnedCompanyName || "",
              companyType: returnedCompanyType || "",
              title: returnedTitle || "",
              datasource: returnedDatasource || "userregistration",
              profession: professionArray,
              professionOther: returnedProfessionOther || "",
              interests: interestsArray,
              interestsOther: returnedInterestsOther || "",
              agreeToTerms: returnedAgreeToTerms || false,
            }));
          }

          // If new partial ID returned, store it
          const returnedId = out_cidn;

          if (returnedId && !cidn) {
            localStorage.setItem("partial_contact_id", returnedId);
            setCIDN(returnedId);
          }
        } catch (error) {
          console.error("Error auto-saving contact:", error);
        }
      },
      600
    ) // control debounce timing here
  ).current;

  const parsePgArray = (value: any): string[] => {
    if (!value) return [];

    // already a JS array
    if (Array.isArray(value)) return value;

    // If comes back as PG array: {"Engineer","Scientist"}
    if (typeof value === "string") {
      const trimmed = value.replace(/^{|}$/g, "").replace(/"/g, "");
      if (!trimmed) return [];
      return trimmed.split(",").map((v) => v.trim());
    }

    return [];
  };

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Copy of updated form data
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // If the field is email and has a value, check for duplicates
      // Only if not in edit mode
      if (name === "email" && value && isNewUser) {
        (async () => {
          try {
            const exists = await checkEmailExists(value);

            if (exists) {
              setFormErrors((prevErrors) => ({
                ...prevErrors,
                email: "This Email is already registered",
              }));
            } else {
              //console.log("Email is available");
              setFormErrors((prevErrors) => ({
                ...prevErrors,
                email: "",
              }));

              debouncedSaveRef(updated, cidn, name);
            }
          } catch (err) {
            console.error("Error checking email:", err);
          }
        })();
      } else {
        debouncedSaveRef(updated, cidn, name);
      }

      return updated;
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;

    if (type === "checkbox") {
      if (name === "agreeToTerms") {
        setFormData((prev) => {
          const updated = {
            ...prev,
            [name]: checked, // checkbox value
          };

          return updated;
        });
      } else if (name === "profession" || name === "interests") {
        const optionValue = value;

        setFormData((prev) => {
          const currentArray = prev[name] as string[];

          const updatedArray = checked
            ? [...currentArray, optionValue]
            : currentArray.filter((item) => item !== optionValue);

          const updated = {
            ...prev,
            [name]: updatedArray,
          };

          return updated;
        });
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: name === "age" ? (value ? Number(value) : undefined) : value,
      }));

      // Update form state
      if (name === "state") {
        setFormData((prev) => {
          const updated = { ...prev, [name]: value };

          return updated;
        });
      }
    }

    // Clear error for this field when user changes it
    if (errors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleRoleToggle = (roleId: string) => {
    setSelectedRoles((prevSelectedRoles) => {
      const stringRoleId = String(roleId);

      const matchFound = prevSelectedRoles.some(
        (id) => String(id) === stringRoleId
      );

      let newRoles: string[];

      if (matchFound) {
        newRoles = prevSelectedRoles.filter(
          (id) => String(id) !== stringRoleId
        );
      } else {
        newRoles = [...prevSelectedRoles, stringRoleId];
      }

      setFormData((prev) => ({
        ...prev,
        roles: newRoles.map((id) => String(id)), // Ensure all role IDs are strings
      }));

      return newRoles;
    });
  };

  const validateEmailBeforeSubmit = async (): Promise<boolean> => {
    if (!isNewUser) return true; // skip check for edits

    if (!formData.email) {
      setFormErrors((prev) => ({
        ...prev,
        email: "Email is required",
      }));
      return false;
    }

    const exists = await checkEmailExists(formData.email);

    if (exists) {
      setFormErrors((prev) => ({
        ...prev,
        email: "This Email is already registered",
      }));

      // scroll to email field
      document
        .querySelector('input[name="email"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });

      return false;
    }

    // clear email error if valid
    setFormErrors((prev) => ({
      ...prev,
      email: undefined,
    }));

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Prevent multiple submissions
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // if email exists and its a new user registration
      // display error and don't submit new user
      const emailIsValid = await validateEmailBeforeSubmit();
      if (!emailIsValid) {
        setIsSubmitting(false);
        return;
      }

      const updatedFormData = {
        ...formData,
        roles: selectedRoles.map((id) => String(id)), // Ensure all role IDs are strings
        cidn: cidn ?? undefined, // include it only if it exists
      };

      if (!isNewUser && userId) {
        updatedFormData.id = userId;
        // Remove password field for existing users
        delete updatedFormData.password;
        // Remove read-only fields that shouldn't be updated
        delete updatedFormData.mndaStatus;
        delete updatedFormData.lastLoginAt;
      }
      const addedbyID = user?.id ?? "admin";

      // use the latest input value, not user.phone
      const phoneInput = formData.phone?.trim() || "";

      let derivedCountryCode = countrycode;
      let derivedPhoneNoCode = phoneInput;

      if (phoneInput) {
        const cleaned = phoneInput.replace(/\D/g, "");

        if (cleaned.length === 11 && cleaned.startsWith("1")) {
          derivedCountryCode = cleaned[0]; // '1'
          derivedPhoneNoCode = cleaned.slice(1); // remove country code
        } else {
          derivedPhoneNoCode = cleaned; // just digits
        }
      }

      // update UI state if needed
      setCountryCode(derivedCountryCode);
      setPhoneNoCode(derivedPhoneNoCode);

      console.log("Updated form data: ", updatedFormData);

      if (isNewUser) {
        await createUser(updatedFormData, addedbyID);

        const response = await saveContact(
          {
            ...updatedFormData,
            countrycode: derivedCountryCode,
            phonenocode: derivedPhoneNoCode,
            agreeToTerms: null,
            datasource: "userregistration",
            ipaddress: "",
          },
          false,
          cidn
        );
      } else if (userId) {
        await updateUser(userId, updatedFormData, addedbyID);

        const cidnToUse: number | null = updatedFormData.cidn ?? null;

        const response = await saveContact(
          {
            ...updatedFormData,
            countrycode: derivedCountryCode,
            phonenocode: derivedPhoneNoCode,
            agreeToTerms: null,
            datasource: "",
            ipaddress: "",
          },
          false,
          cidnToUse
        );
      }

      navigate("/user-management");
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="generalPage">
      <Header2 />
      <div className="landingPage">
        <div className="user-form-container">
          <div className="user-form-header">
            <h1 className="user-form-title">
              {isNewUser ? "Add New User" : "Edit User"}
            </h1>
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="user-form">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className="form-input"
                required
              />
              {errors.email && (
                <div className="field-error">{errors.email}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">First Name</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Middle Name</label>
              <input
                type="text"
                name="middleName"
                value={formData.middleName || ""}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName || ""}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Address</label>
              <input
                type="text"
                name="streetaddress"
                value={formData.streetaddress || ""}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">City</label>
              <input
                type="text"
                name="city"
                value={formData.city || ""}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">State</label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
              >
                <option value="">-- Select State --</option>
                {states.map((s) => (
                  <option
                    key={s.stateprovinceabbrv}
                    value={s.stateprovinceabbrv}
                  >
                    {s.stateprovincename}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Zip</label>
              <input
                type="text"
                name="zip"
                value={formData.zip || ""}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Country</label>
              <input
                type="text"
                name="country"
                value={formData.country || ""}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone || ""}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Company Name</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName || ""}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Company Type</label>
              <input
                type="text"
                name="companyType"
                value={formData.companyType || ""}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title || ""}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            {!isNewUser && (
              <div className="form-group">
                <label className="form-label">Entity ID</label>
                <select
                  name="entityid"
                  value={formData.entityid || ""}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="">Select Entity</option>
                  <option value="1">IceGems</option>
                  <option value="2">Zimtex Energy, LLC</option>
                  <option value="3">Zimtex Energy II, LLC</option>
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Professions</label>
              <div className="checkbox-group">
                {PROFESSIONS.map((prof) => (
                  <div key={prof} className="checkbox-item">
                    <input
                      type="checkbox"
                      id={`profession-${prof}`}
                      name="profession"
                      value={prof}
                      checked={formData.profession?.includes(prof) || false}
                      onChange={handleChange}
                      className={errors.profession ? "error" : ""}
                    />
                    <label htmlFor={`profession-${prof}`}> {prof}</label>
                  </div>
                ))}
              </div>
              {formData.profession?.includes("Other") && (
                <input
                  type="text"
                  name="professionOther"
                  value={formData.professionOther || ""}
                  onChange={handleChange}
                  placeholder="Please specify other profession"
                  className="form-input other-input"
                />
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Interests</label>
              <div className="checkbox-group">
                {INTERESTS.map((interest) => (
                  <div key={interest} className="checkbox-item">
                    <input
                      type="checkbox"
                      id={`interest-${interest}`}
                      name="interests"
                      value={interest}
                      checked={formData.interests?.includes(interest) || false}
                      onChange={handleChange}
                      className={errors.interests ? "error" : ""}
                    />
                    <label htmlFor={`interest-${interest}`}> {interest}</label>
                  </div>
                ))}
              </div>
              {formData.interests?.includes("Other") && (
                <input
                  type="text"
                  name="interestsOther"
                  value={formData.interestsOther || ""}
                  onChange={handleChange}
                  placeholder="Please specify other interest"
                  className="form-input other-input"
                />
              )}
            </div>

            <div className="form-group">
              <label className="form-label">MNDA Status</label>
              <input
                type="text"
                name="mndaStatus"
                value={formData.mndaStatus || ""}
                readOnly
                className="form-input readonly-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Roles</label>
              <div className="role-checkboxes">
                {roles.length === 0 ? (
                  <div>Loading roles...</div>
                ) : (
                  roles.map((role) => {
                    const roleIdString = String(role.id);
                    const isChecked = selectedRoles.some(
                      (id) => String(id) === roleIdString
                    );

                    return (
                      <div key={role.id} className="role-checkbox-item">
                        <input
                          type="checkbox"
                          id={`role-${role.id}`}
                          checked={isChecked}
                          onChange={() => handleRoleToggle(role.id)}
                          className="role-checkbox"
                        />
                        <label
                          htmlFor={`role-${role.id}`}
                          className="role-label"
                        >
                          {role.name}
                        </label>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {isNewUser && (
              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password || ""}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate("/user-management")}
                className="cancel-button"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    {isNewUser ? "Adding User..." : "Updating User..."}
                  </>
                ) : isNewUser ? (
                  "Add User"
                ) : (
                  "Update User"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
